import { PrReviewPolicyConfig } from "../agent-config-options";

/**
 * Default path globs that exempt a PR from the `human-required.size`
 * rule. The policy walks every changed path in the PR and skips
 * rule #6 (size threshold) when **every** path matches at least one
 * glob in this list. Doc-only PRs routinely exceed the 500-insertion
 * threshold (large migrations, bulk additions, refresh passes) but
 * carry no production risk that warrants forcing a human reviewer.
 *
 * The default exempts the entire `docs/**` tree — every consumer of
 * configulator places its Starlight docs site there. Consumers can
 * extend this list (e.g. add `docs/research/**` if doc-style research
 * notes live outside the Starlight tree) by passing
 * `prReviewPolicy.autoMerge.pathsExemptFromSize`.
 *
 * @see PrReviewPolicyConfig
 * @see PrReviewAutoMergeConfig.pathsExemptFromSize
 */
export const DEFAULT_PATHS_EXEMPT_FROM_SIZE: ReadonlyArray<string> = [
  "docs/**",
];

/**
 * Default required-workflow list for the CI-verification Actions-runs
 * fallback. Empty by design: when the reviewer has to fall back to
 * the Actions runs API (because the primary check-runs read returned
 * a fine-grained-PAT 403), an empty list means **every** workflow run
 * observed for the PR head SHA is treated as required. That is the
 * conservative zero-config default — an unknown failing workflow
 * blocks auto-merge rather than slipping through.
 *
 * Consumers that want to gate on a known subset (e.g. ignore an
 * optional or advisory workflow) set
 * `prReviewPolicy.ciVerification.requiredWorkflows`.
 *
 * @see PrReviewCiVerificationConfig
 * @see PrReviewCiVerificationConfig.requiredWorkflows
 */
export const DEFAULT_REQUIRED_WORKFLOWS: ReadonlyArray<string> = [];

/**
 * Fully-resolved PR review policy. Every field is defaulted so
 * downstream renderers can reason about a single canonical shape.
 *
 * Two sub-rules are configurable today: the doc-only carve-out
 * against the size threshold (`autoMerge.pathsExemptFromSize`) and
 * the CI-verification fallback's required-workflow list
 * (`ciVerification.requiredWorkflows`). Additional knobs for other
 * rules in the policy may be added in future versions of
 * `PrReviewPolicyConfig`.
 */
export interface ResolvedPrReviewPolicy {
  readonly autoMerge: ResolvedPrReviewAutoMerge;
  readonly ciVerification: ResolvedPrReviewCiVerification;
}

/**
 * Fully-resolved `auto-merge` half of the policy.
 *
 * `pathsExemptFromSize` is always populated — the default
 * (`["docs/**"]`) ships when the consumer omits the option.
 */
export interface ResolvedPrReviewAutoMerge {
  readonly pathsExemptFromSize: ReadonlyArray<string>;
}

/**
 * Fully-resolved `ci-verification` half of the policy.
 *
 * `requiredWorkflows` is always populated — the default (`[]`, i.e.
 * "treat every observed Actions run as required") ships when the
 * consumer omits the option.
 */
export interface ResolvedPrReviewCiVerification {
  readonly requiredWorkflows: ReadonlyArray<string>;
}

/**
 * Resolve a (possibly absent) `PrReviewPolicyConfig` into a canonical
 * `ResolvedPrReviewPolicy` with every field filled in. Unset fields
 * cascade from their documented defaults.
 *
 * Malformed configs (empty / whitespace-only path entries) throw a
 * descriptive `Error` — callers should not need to guard against it
 * at runtime.
 */
export function resolvePrReviewPolicy(
  config?: PrReviewPolicyConfig,
): ResolvedPrReviewPolicy {
  const pathsExemptFromSize =
    config?.autoMerge?.pathsExemptFromSize ?? DEFAULT_PATHS_EXEMPT_FROM_SIZE;
  const requiredWorkflows =
    config?.ciVerification?.requiredWorkflows ?? DEFAULT_REQUIRED_WORKFLOWS;

  assertValidPathsExemptFromSize(pathsExemptFromSize);
  assertValidRequiredWorkflows(requiredWorkflows);

  return {
    autoMerge: {
      pathsExemptFromSize: [...pathsExemptFromSize],
    },
    ciVerification: {
      requiredWorkflows: [...requiredWorkflows],
    },
  };
}

/**
 * Synth-time validation hook. Throws a descriptive `Error` when the
 * supplied `PrReviewPolicyConfig` is malformed. Called by
 * `AgentConfig.preSynthesize` before any rendering so a misconfigured
 * policy fails the build instead of silently shipping broken carve-out
 * globs. Returns the resolved policy unchanged so callers can write
 * `const policy = validatePrReviewPolicyConfig(config)` in one line.
 *
 * Malformed cases rejected here:
 *
 * - `pathsExemptFromSize` entries that are empty or whitespace-only —
 *   such an entry would either silently match nothing or match every
 *   path, both of which are almost certainly a typo.
 * - `requiredWorkflows` entries that are empty or whitespace-only — a
 *   blank workflow name can never match an Actions-run `name`, so the
 *   intended gate would silently never fire.
 */
export function validatePrReviewPolicyConfig(
  config?: PrReviewPolicyConfig,
): ResolvedPrReviewPolicy {
  return resolvePrReviewPolicy(config);
}

function assertValidPathsExemptFromSize(paths: ReadonlyArray<string>): void {
  for (const path of paths) {
    if (typeof path !== "string" || path.trim().length === 0) {
      throw new Error(
        "prReviewPolicy.autoMerge.pathsExemptFromSize entries must be non-empty strings",
      );
    }
  }
}

function assertValidRequiredWorkflows(workflows: ReadonlyArray<string>): void {
  for (const workflow of workflows) {
    if (typeof workflow !== "string" || workflow.trim().length === 0) {
      throw new Error(
        "prReviewPolicy.ciVerification.requiredWorkflows entries must be non-empty strings",
      );
    }
  }
}
